import React, { useEffect, useState } from 'react';
import { ChevronRight, Shield, User, MapPin } from 'lucide-react';

interface RedirectModalProps {
  isOpen: boolean;
  role: string;
  redirectUrl: string;
  userName?: string;
  moduleName?: string;
}

const RedirectModal: React.FC<RedirectModalProps> = ({
  isOpen,
  role,
  redirectUrl,
  userName = 'Utilisateur',
  moduleName = 'votre module'
}) => {
  const [countdown, setCountdown] = useState(3);
  const [progress, setProgress] = useState(0);

  // Déterminer le nom du module à partir de l'URL
  const getModuleName = (url: string): string => {
    if (url.includes('/identification')) return 'Identification';
    if (url.includes('/mobilisation')) return 'Mobilisation';
    if (url.includes('/parrainages')) return 'Parrainages';
    if (url.includes('/call-center')) return 'Centre d\'Appels';
    if (url.includes('/bureaux')) return 'Bureaux';
    return 'votre espace de travail';
  };

  // Déterminer la couleur selon le module
  const getModuleColor = (url: string): string => {
    if (url.includes('/identification')) return 'from-blue-500 to-blue-600';
    if (url.includes('/mobilisation')) return 'from-purple-500 to-purple-600';
    if (url.includes('/parrainages')) return 'from-orange-500 to-orange-600';
    if (url.includes('/call-center')) return 'from-green-500 to-green-600';
    return 'from-gray-500 to-gray-600';
  };

  // Déterminer l'icône du rôle
  const getRoleIcon = (role: string) => {
    if (role.includes('AGENT')) return <User className="w-6 h-6" />;
    if (role.includes('BENEVOLE')) return <User className="w-6 h-6" />;
    if (role.includes('RBV')) return <MapPin className="w-6 h-6" />;
    return <Shield className="w-6 h-6" />;
  };

  // Formater le nom du rôle
  const formatRole = (role: string): string => {
    const roleNames: Record<string, string> = {
      'AGENT_SAISIE': 'Agent de Saisie',
      'AGENT_IDENTIFICATION': 'Agent d\'Identification',
      'AGENT_CALL_PROSPECTION': 'Agent Call Center (Prospection)',
      'AGENT_CALL_QUALIFICATION': 'Agent Call Center (Qualification)',
      'BENEVOLE_MOBILISATION': 'Bénévole Mobilisation',
      'RBV': 'Responsable Bureau de Vote'
    };
    return roleNames[role] || role;
  };

  useEffect(() => {
    if (!isOpen) return;

    // Animation de progression
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 2, 100));
    }, 60);

    // Compte à rebours
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          clearInterval(progressInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(countdownInterval);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const displayModuleName = moduleName !== 'votre module' ? moduleName : getModuleName(redirectUrl);
  const moduleColor = getModuleColor(redirectUrl);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop avec blur */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scale-in">
        {/* Header avec gradient */}
        <div className={`bg-gradient-to-r ${moduleColor} p-6 text-white`}>
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-full">
              {getRoleIcon(role)}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center">
            Bienvenue, {userName} !
          </h2>
          <p className="text-center text-white/90 mt-2">
            {formatRole(role)}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-gray-600 text-lg">
              Vous allez être redirigé vers
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-2 flex items-center justify-center gap-2">
              <ChevronRight className="w-6 h-6 text-orange-500 animate-pulse" />
              {displayModuleName}
            </p>
          </div>

          {/* Barre de progression */}
          <div className="mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${moduleColor} transition-all duration-100 ease-linear`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Countdown */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <span className="text-3xl font-bold text-gray-800">{countdown}</span>
            </div>
            <p className="text-sm text-gray-500">
              Redirection automatique...
            </p>
          </div>

          {/* Info box */}
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800 text-center">
              Le portail principal est réservé aux administrateurs et superviseurs.
              Votre espace de travail personnel vous attend !
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 text-center border-t">
          <p className="text-xs text-gray-500">
            powered by <span className="font-semibold">RED<span className="text-red-600">LINE</span></span>
          </p>
        </div>
      </div>

      {/* Styles d'animation */}
      <style>{`
        @keyframes scale-in {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default RedirectModal;
